import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Building2, MapPin, Pencil, Users, X } from 'lucide-react';
import { toast } from '../../../../../ui/toast';
import { getEmployeeList } from '../../../../../utils/api_service/employees';
import { getPartnerEmployees } from '../../../../../utils/api_service/partner_api';
import { debounce } from '../../../../../utils/helpers/debounce';
import {
  EmployeeUser,
  PartnerEntity,
  PartnerUser,
} from '../../../../../utils/types/admin_types';
import { SelectOptions } from '../../../../../utils/types/react_select_types';

export interface EditPartnerModalProps {
  open: boolean;
  onClose: () => void;
  entityList: PartnerEntity[];
  entity: PartnerEntity | undefined;
  employees: EmployeeUser[];
  partnerEmployees: PartnerUser[];
  editPartnerEntity: (
    entityList: PartnerEntity[],
    entity: PartnerEntity,
    internalManagers: SelectOptions[],
    externalManagers: SelectOptions[],
  ) => void;
}

const partnerValidationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .required('Partner Entity Name is required')
    .min(2, 'Name must be at least 2 characters'),
  alias: Yup.string().trim(),
  city: Yup.string().trim(),
  state: Yup.string().trim(),
  country: Yup.string().trim(),
  region: Yup.string().trim(),
  continent: Yup.string().trim(),
});

const isEmployeeArray = (
  array: unknown,
): array is (EmployeeUser | PartnerUser)[] => {
  return (
    Array.isArray(array) &&
    array.length > 0 &&
    typeof array[0] === 'object' &&
    array[0] !== null &&
    'user' in array[0]
  );
};

export const EditPartnerModal: React.FC<EditPartnerModalProps> = ({
  open,
  onClose,
  entityList,
  entity,
  employees,
  partnerEmployees,
  editPartnerEntity,
}) => {
  const [internalOptions, setInternalOptions] = useState<SelectOptions[]>([]);
  const [externalOptions, setExternalOptions] = useState<SelectOptions[]>([]);
  const [selectedInternalManagers, setSelectedInternalManagers] = useState<
    SelectOptions[]
  >([]);
  const [selectedExternalManagers, setSelectedExternalManagers] = useState<
    SelectOptions[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync initial options from props
  useEffect(() => {
    const internalOpts: SelectOptions[] = (employees || []).map((emp) => ({
      value: emp.id,
      label: emp.user ? `${emp.user.firstName} ${emp.user.lastName}` : emp.id,
    }));
    setInternalOptions(internalOpts);

    const externalOpts: SelectOptions[] = (partnerEmployees || []).map((emp) => ({
      value: emp.id,
      label: emp.user ? `${emp.user.firstName} ${emp.user.lastName}` : emp.id,
    }));
    setExternalOptions(externalOpts);
  }, [employees, partnerEmployees]);

  // Sync entity data when modal opens or entity changes
  useEffect(() => {
    if (!entity) return;

    if (isEmployeeArray(entity.partnerManagersInternal)) {
      setSelectedInternalManagers(
        entity.partnerManagersInternal.map((emp) => ({
          value: emp.id,
          label: emp.user ? `${emp.user.firstName} ${emp.user.lastName}` : emp.id,
        })),
      );
    } else if (Array.isArray(entity.partnerManagersInternal)) {
      const ids = new Set(entity.partnerManagersInternal as string[]);
      const matched = (employees || [])
        .filter((e) => ids.has(e.id))
        .map((e) => ({
          value: e.id,
          label: e.user ? `${e.user.firstName} ${e.user.lastName}` : e.id,
        }));
      setSelectedInternalManagers(matched);
    } else {
      setSelectedInternalManagers([]);
    }

    if (isEmployeeArray(entity.partnerManagersExternal)) {
      setSelectedExternalManagers(
        entity.partnerManagersExternal.map((emp) => ({
          value: emp.id,
          label: emp.user ? `${emp.user.firstName} ${emp.user.lastName}` : emp.id,
        })),
      );
    } else if (Array.isArray(entity.partnerManagersExternal)) {
      const ids = new Set(entity.partnerManagersExternal as string[]);
      const matched = (partnerEmployees || [])
        .filter((e) => ids.has(e.id))
        .map((e) => ({
          value: e.id,
          label: e.user ? `${e.user.firstName} ${e.user.lastName}` : e.id,
        }));
      setSelectedExternalManagers(matched);
    } else {
      setSelectedExternalManagers([]);
    }
  }, [entity, employees, partnerEmployees]);

  // Debounced search for employee lists
  const debouncedFetchEmployees = useMemo(
    () =>
      debounce(async (query: string, isInternal: boolean) => {
        if (!query.trim()) return;
        try {
          if (isInternal) {
            const res = await getEmployeeList(query, 1, 10);
            if (res.ok) {
              const data = await res.json();
              const opts = data.employee.map((e: EmployeeUser) => ({
                value: e.id,
                label: `${e.user.firstName} ${e.user.lastName}`,
              }));
              setInternalOptions((prev) => {
                const map = new Map(prev.map((o) => [o.value, o]));
                opts.forEach((o: SelectOptions) => map.set(o.value, o));
                return Array.from(map.values());
              });
            }
          } else {
            const res = await getPartnerEmployees({
              page: 1,
              recordsPerPage: 10,
              query,
            });
            if (res.ok) {
              const data = await res.json();
              const opts = data.employee.map((e: EmployeeUser) => ({
                value: e.id,
                label: `${e.user.firstName} ${e.user.lastName}`,
              }));
              setExternalOptions((prev) => {
                const map = new Map(prev.map((o) => [o.value, o]));
                opts.forEach((o: SelectOptions) => map.set(o.value, o));
                return Array.from(map.values());
              });
            }
          }
        } catch (error) {
          toast.error('Error fetching manager options: ' + error, {
            position: 'bottom-center',
          });
        }
      }, 400),
    [],
  );

  const formik = useFormik({
    initialValues: {
      name: entity?.name || '',
      alias: entity?.alias || '',
      city: entity?.location?.city || (entity as any)?.city || '',
      state:
        entity?.location?.state ||
        (entity as any)?.countryRegion ||
        (entity as any)?.state ||
        '',
      country: entity?.location?.country || (entity as any)?.country || '',
      region: entity?.location?.region || (entity as any)?.region || '',
      continent:
        entity?.location?.continent || (entity as any)?.continent || '',
    },
    enableReinitialize: true,
    validationSchema: partnerValidationSchema,
    onSubmit: async (values) => {
      if (!entity) return;
      setIsSubmitting(true);
      try {
        const updatedEntity: PartnerEntity = {
          ...entity,
          name: values.name.trim(),
          alias: values.alias.trim(),
          location: {
            city: values.city.trim(),
            state: values.state.trim(),
            country: values.country.trim(),
            region: values.region.trim(),
            continent: values.continent.trim(),
          },
          partnerManagersInternal: selectedInternalManagers.map((m) => m.value),
          partnerManagersExternal: selectedExternalManagers.map((m) => m.value),
        };

        editPartnerEntity(
          entityList,
          updatedEntity,
          selectedInternalManagers,
          selectedExternalManagers,
        );
        onClose();
      } catch (err) {
        toast.error('Failed to update partner entity: ' + err, {
          position: 'bottom-center',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        },
      }}
    >
      <DialogTitle
        sx={{
          py: 2,
          px: 3,
          bgcolor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              bgcolor: 'rgba(37, 99, 235, 0.1)',
              color: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Pencil size={18} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a' }}
            >
              Edit Partner Entity
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Update partner organization details, managers, and location
            </Typography>
          </Box>
        </Stack>

        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: '#64748b',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.05)', color: '#0f172a' },
          }}
        >
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2.5}>
            {/* General Information */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Building2 size={16} style={{ color: '#2563eb' }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  General Information
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    fullWidth
                    required
                    id="name"
                    name="name"
                    label="Partner Entity Name"
                    placeholder="e.g. Cargills Pvt Ltd"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                    InputProps={{ sx: { borderRadius: '8px' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    fullWidth
                    id="alias"
                    name="alias"
                    label="Partner Entity Alias"
                    placeholder="e.g. Cargills Colombo"
                    value={formik.values.alias}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.alias && Boolean(formik.errors.alias)}
                    helperText={formik.touched.alias && formik.errors.alias}
                    InputProps={{ sx: { borderRadius: '8px' } }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ borderColor: '#f1f5f9' }} />

            {/* Managers */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Users size={16} style={{ color: '#4f46e5' }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  Partner Managers
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={internalOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    value={selectedInternalManagers}
                    onChange={(_, newValue) =>
                      setSelectedInternalManagers(newValue)
                    }
                    onInputChange={(_, value) =>
                      debouncedFetchEmployees(value, true)
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          label={option.label}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.725rem',
                            bgcolor: 'rgba(37, 99, 235, 0.08)',
                            color: '#1d4ed8',
                            fontWeight: 500,
                          }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Internal Partner Managers"
                        placeholder="Search & select internal managers..."
                        InputProps={{ ...params.InputProps, sx: { borderRadius: '8px' } }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={externalOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    value={selectedExternalManagers}
                    onChange={(_, newValue) =>
                      setSelectedExternalManagers(newValue)
                    }
                    onInputChange={(_, value) =>
                      debouncedFetchEmployees(value, false)
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          label={option.label}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.725rem',
                            bgcolor: 'rgba(16, 185, 129, 0.08)',
                            color: '#047857',
                            fontWeight: 500,
                          }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="External Partner Managers"
                        placeholder="Search & select external managers..."
                        InputProps={{ ...params.InputProps, sx: { borderRadius: '8px' } }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ borderColor: '#f1f5f9' }} />

            {/* Location Details */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <MapPin size={16} style={{ color: '#0284c7' }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  Location & Geography
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    fullWidth
                    id="city"
                    name="city"
                    label="City"
                    placeholder="e.g. Colombo"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    InputProps={{ sx: { borderRadius: '8px' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    fullWidth
                    id="state"
                    name="state"
                    label="Country Region / State"
                    placeholder="e.g. Western Province"
                    value={formik.values.state}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    InputProps={{ sx: { borderRadius: '8px' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    fullWidth
                    id="country"
                    name="country"
                    label="Country"
                    placeholder="e.g. Sri Lanka"
                    value={formik.values.country}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    InputProps={{ sx: { borderRadius: '8px' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    fullWidth
                    id="region"
                    name="region"
                    label="Region"
                    placeholder="e.g. South Asia"
                    value={formik.values.region}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    InputProps={{ sx: { borderRadius: '8px' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    fullWidth
                    id="continent"
                    name="continent"
                    label="Continent"
                    placeholder="e.g. Asia"
                    value={formik.values.continent}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    InputProps={{ sx: { borderRadius: '8px' } }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            py: 2,
            px: 3,
            bgcolor: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              borderColor: '#e2e8f0',
              color: '#64748b',
              fontWeight: 600,
              px: 2,
              '&:hover': { borderColor: '#cbd5e1', bgcolor: '#ffffff' },
            }}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              px: 2.5,
              bgcolor: '#2563eb',
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
              '&:hover': {
                bgcolor: '#1d4ed8',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.35)',
              },
            }}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
